/**
 * Accounting — yearly report (owners/admins), the section above the payroll
 * report and in the same layout. Material costs pulled from the Crossroads +
 * Hilti master databases (invoice PDFs + account-statement rows, never counted
 * twice), QuickBooks invoices sent / paid / owing / overdue, and the overhead
 * buckets that are still coming (vehicles, insurance, app, cellular, 407,
 * bonuses, subscriptions). Views: Summary · Invoices (QuickBooks detail).
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, FileSpreadsheet, FileText, Loader2, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { downloadBlob } from "../payroll/downloadBlob";
import InvoicesSentView from "./InvoicesSentView";

const money = (v) => `$${Number(v || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthName = (ym) => new Date(`${ym}-15T00:00:00`).toLocaleDateString("en-CA", { month: "short" });

export const StatCard = ({ label, value, sub, accent, testId, muted }) => (
  <div className={`rounded-lg border border-white/10 bg-white/[0.02] p-3 ${muted ? "opacity-60" : ""}`} data-testid={testId}>
    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50">{label}</p>
    <p className={`mt-1 font-mono text-lg font-bold tabular-nums ${accent || "text-white"}`}>{value}</p>
    {sub && <div className="mt-0.5 font-mono text-[10px] text-white/50">{sub}</div>}
  </div>
);

const PILL = "rounded-md px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest";
const BTN = "inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-white/70 hover:border-[#1a3fd1] hover:text-white disabled:opacity-50";

export default function AccountingYearReport() {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("summary");    // "summary" | "invoices"
  const [quarter, setQuarter] = useState(0);
  const [exporting, setExporting] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [, setParams] = useSearchParams();

  const load = useCallback(async (yr, q) => {
    setLoading(true);
    try {
      const r = await api.get("/accounting/year-report", { params: { year: yr, ...(q ? { quarter: q } : {}) } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the accounting report");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(year, quarter); }, [load, year, quarter]);

  const exportReport = async (format) => {
    setExporting(format);
    try {
      const r = await api.get("/accounting/year-report-export", { params: { year, format, ...(quarter ? { quarter } : {}) }, responseType: "blob" });
      downloadBlob(r.data, `accounting_report_${year}${quarter ? `_Q${quarter}` : ""}.${format}`, r.headers["content-disposition"]);
      toast.success(`Accounting report ${year}${quarter ? ` Q${quarter}` : ""} downloaded as ${format.toUpperCase()}`);
    } catch {
      toast.error("Could not export the report");
    } finally {
      setExporting(null);
    }
  };

  const syncQbo = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/billing/sync-qbo-invoices");
      toast.success(`QuickBooks synced — ${r.data.synced} invoices`);
      await load(year, quarter);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sync QuickBooks");
    } finally {
      setSyncing(false);
    }
  };

  const m = data?.materials || {};
  const inv = data?.invoices || {};
  const years = data?.years || [year];
  const canPrev = year > (years[0] ?? year) - 1;
  const canNext = year < new Date().getFullYear();
  const windowLabel = `${year}${quarter ? ` · Q${quarter}` : ""}${!quarter && year === new Date().getFullYear() ? " · to date" : ""}`;

  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="accounting-year-report">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Accounting · yearly report</p>
          <h2 className="mt-1 text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl" data-testid="accounting-report-title">
            {view === "invoices" ? "Billed in" : "Spent & billed in"} {windowLabel}
          </h2>
          <p className="mt-1 font-mono text-[10px] text-white/50">
            Materials from the Crossroads + Hilti databases (invoice PDFs + statement rows, never counted twice) · invoices from QuickBooks · overhead buckets coming soon.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center rounded-lg border border-white/10 bg-white/[0.02] p-1" role="tablist" data-testid="accounting-report-view">
            {[["summary", "Summary"], ["invoices", "Invoices"]].map(([k, lb]) => (
              <button key={k} type="button" role="tab" aria-selected={view === k} onClick={() => setView(k)} data-testid={`accounting-report-view-${k}`}
                className={`${PILL} px-3 ${view === k ? "bg-[#1a3fd1] text-white shadow" : "text-white/70 hover:text-white"}`}>
                {lb}
              </button>
            ))}
          </div>
          <div className="flex items-center rounded-lg border border-white/10 bg-white/[0.02] p-1" role="tablist" data-testid="accounting-report-quarter">
            {[[0, "Year"], [1, "Q1"], [2, "Q2"], [3, "Q3"], [4, "Q4"]].map(([q, lb]) => (
              <button key={q} type="button" role="tab" aria-selected={quarter === q} onClick={() => setQuarter(q)} data-testid={`accounting-report-quarter-${q}`}
                title={q ? `${["Jan–Mar", "Apr–Jun", "Jul–Sep", "Oct–Dec"][q - 1]} ${year}` : `All of ${year}`}
                className={`${PILL} ${quarter === q ? "bg-[#1a3fd1] text-white shadow" : "text-white/70 hover:text-white"}`}>
                {lb}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.02] p-1" data-testid="accounting-report-export">
            <button type="button" onClick={() => exportReport("csv")} disabled={!!exporting} data-testid="accounting-report-export-csv" title="Download as a spreadsheet for the accountant" className={BTN}>
              {exporting === "csv" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />} CSV
            </button>
            <button type="button" onClick={() => exportReport("pdf")} disabled={!!exporting} data-testid="accounting-report-export-pdf" title="Download as a PDF" className={BTN}>
              {exporting === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} PDF
            </button>
            <button type="button" onClick={syncQbo} disabled={syncing} data-testid="accounting-report-sync-qbo"
              title="Pull the latest invoices and payments from QuickBooks"
              className="inline-flex items-center gap-1.5 rounded-md border border-[#1a3fd1]/60 bg-[#1a3fd1]/15 px-2.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-white hover:bg-[#1a3fd1]/30 disabled:opacity-50">
              <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} /> Sync QBO
            </button>
          </div>
          <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.02] p-1" data-testid="accounting-report-year-nav">
            <button type="button" onClick={() => setYear((y) => y - 1)} disabled={!canPrev} aria-label="Previous year" data-testid="accounting-report-prev"
              className="rounded-md border border-white/10 p-1.5 text-white/70 hover:border-[#1a3fd1] hover:text-white disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
            <span className="min-w-[4.5rem] text-center font-mono text-sm font-bold text-white" data-testid="accounting-report-year">{year}</span>
            <button type="button" onClick={() => setYear((y) => y + 1)} disabled={!canNext} aria-label="Next year" data-testid="accounting-report-next"
              className="rounded-md border border-white/10 p-1.5 text-white/70 hover:border-[#1a3fd1] hover:text-white disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {loading && !data ? (
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white/50" data-testid="accounting-report-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </p>
      ) : !data ? null : view === "invoices" ? (
        <InvoicesSentView data={data} loading={loading} />
      ) : (
        <>
          {/* materials */}
          <div className={`grid grid-cols-2 gap-2 lg:grid-cols-4 ${loading ? "opacity-60" : ""}`} data-testid="accounting-report-materials">
            <StatCard label="Material costs (all vendors)" value={money(m.total)} accent="text-[#1a3fd1] dark:text-[#8fa8ff]"
              sub={`${m.invoices || 0} vendor invoice${m.invoices === 1 ? "" : "s"}`} testId="accounting-report-materials-total" />
            {(m.vendors || []).map((v) => (
              <StatCard key={v.vendor} label={v.label} value={money(v.total)} testId={`accounting-report-vendor-${v.vendor}`}
                sub={`${v.invoices} invoice${v.invoices === 1 ? "" : "s"} · ${v.from_invoices} PDF${v.from_invoices === 1 ? "" : "s"} + ${v.from_statements} from statements`} />
            ))}
            <StatCard label="Linked to jobs" value={money(m.linked_total)} accent="text-emerald-300" testId="accounting-report-linked"
              sub={<>
                <span>{(m.vendors || []).map((v) => `${v.label} ${v.linked_count}/${v.invoices}`).join(" · ")}</span>
                <button type="button" onClick={() => setParams((p) => { const n = new URLSearchParams(p); n.set("tab", "search"); return n; })} data-testid="accounting-report-unlinked"
                  className={`mt-0.5 block text-left font-bold underline-offset-2 hover:underline ${m.unlinked_count > 0 ? "text-red-400" : "text-emerald-300"}`}>
                  {m.unlinked_count > 0 ? `${money(m.unlinked_total)} on ${m.unlinked_count} invoice${m.unlinked_count === 1 ? "" : "s"} not linked to a job — link them` : "Every invoice is linked to a job"}
                </button>
              </>} />
          </div>

          {/* invoices sent */}
          <div className={`grid grid-cols-2 gap-2 sm:grid-cols-4 ${loading ? "opacity-60" : ""}`} data-testid="accounting-report-invoices">
            <StatCard label="Invoices sent" value={money(inv.sent_total)} testId="accounting-report-invoices-sent"
              sub={<>
                <span>{inv.count || 0} invoice{inv.count === 1 ? "" : "s"} · QuickBooks{inv.synced_at ? ` · synced ${inv.synced_at.slice(0, 10)}` : ""}</span>
                {!inv.connected && <span className="block text-amber-300">QuickBooks not connected — showing the last sync</span>}
              </>} />
            <StatCard label="Paid" value={money(inv.paid_total)} accent="text-emerald-300" sub={`${inv.paid_count || 0} paid in full`} testId="accounting-report-invoices-paid" />
            <StatCard label="Owing" value={money(inv.owing_total)} accent={inv.owing_total > 0 ? "text-amber-300" : "text-white"} sub={`${inv.open_count || 0} open`} testId="accounting-report-invoices-owing" />
            <StatCard label="Overdue" value={money(inv.overdue_total)} accent={inv.overdue_total > 0 ? "text-red-400" : "text-white"}
              sub={inv.overdue_count ? `${inv.overdue_count} past due — see Invoices` : "nothing past due"} testId="accounting-report-invoices-overdue" />
          </div>

          {/* overhead - from the Overhead master database (Search -> Overhead) */}
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40">Overhead · {money((data.overhead || []).reduce((a, o) => a + (o.total || 0), 0))}</p>
              <button type="button" onClick={() => setParams((p) => { const n = new URLSearchParams(p); n.set("tab", "search"); n.set("sub", "overhead"); return n; })} data-testid="accounting-report-overhead-open"
                className="font-mono text-[9px] uppercase tracking-widest text-[#8fa8ff] hover:underline">Open Overhead database →</button>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7" data-testid="accounting-report-overhead">
              {(data.overhead || []).map((o) => (
                <StatCard key={o.key} label={o.label} value={money(o.total)} muted={!o.count} testId={`accounting-report-overhead-${o.key}`}
                  sub={o.count
                    ? <>{o.count} bill{o.count === 1 ? "" : "s"}{o.top_vendors?.[0] ? ` · ${o.top_vendors[0].vendor}` : ""}{o.next_renewal && <span className="block text-amber-300">renews {o.next_renewal}</span>}</>
                    : <span className="rounded bg-white/10 px-1.5 py-0.5 font-bold uppercase tracking-wider text-white/60">no bills yet</span>} />
              ))}
            </div>
          </div>

          {/* by month */}
          <div className="overflow-x-auto rounded-lg border border-white/10 bg-white/[0.02]" data-testid="accounting-report-months">
            <table className="w-full min-w-[640px] border-collapse text-xs">
              <thead>
                <tr className="font-mono text-[9px] uppercase tracking-widest text-white/50">
                  <th className="px-3 py-2 text-left font-normal">Month</th>
                  {(m.vendors || []).map((v) => <th key={v.vendor} className="px-3 py-2 text-right font-normal">{v.label}</th>)}
                  <th className="px-3 py-2 text-right font-normal">Materials</th>
                  <th className="px-3 py-2 text-right font-normal">Invoiced</th>
                  <th className="px-3 py-2 text-right font-normal">Collected</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {(() => {
                  const im = Object.fromEntries((inv.by_month || []).map((x) => [x.month, x]));
                  const mm = Object.fromEntries((m.by_month || []).map((x) => [x.month, x]));
                  const months = [...new Set([...Object.keys(mm), ...Object.keys(im)])].sort();
                  if (!months.length) return <tr><td colSpan={(m.vendors || []).length + 4} className="px-3 py-6 text-center font-mono text-[11px] text-white/50">Nothing recorded in {windowLabel}.</td></tr>;
                  return months.map((ym) => (
                    <tr key={ym} className="font-mono tabular-nums text-white/80" data-testid={`accounting-report-month-${ym}`}>
                      <td className="px-3 py-1.5 font-sans font-semibold text-white">{monthName(ym)} {ym.slice(0, 4)}</td>
                      {(m.vendors || []).map((v) => <td key={v.vendor} className="px-3 py-1.5 text-right">{money(mm[ym]?.[v.vendor])}</td>)}
                      <td className="px-3 py-1.5 text-right font-bold text-white">{money(mm[ym]?.total)}</td>
                      <td className="px-3 py-1.5 text-right">{money(im[ym]?.sent)}</td>
                      <td className="px-3 py-1.5 text-right text-emerald-300">{money(im[ym]?.paid)}</td>
                    </tr>
                  ));
                })()}
              </tbody>
              <tfoot>
                <tr className="border-t border-white/20 font-mono text-xs font-bold tabular-nums text-white">
                  <td className="px-3 py-2 font-sans">Total</td>
                  {(m.vendors || []).map((v) => <td key={v.vendor} className="px-3 py-2 text-right">{money(v.total)}</td>)}
                  <td className="px-3 py-2 text-right">{money(m.total)}</td>
                  <td className="px-3 py-2 text-right">{money(inv.sent_total)}</td>
                  <td className="px-3 py-2 text-right text-emerald-300">{money(inv.paid_total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* top jobs per vendor */}
          {(m.vendors || []).some((v) => v.top_jobs?.length > 0) && (
            <div className="grid gap-2 sm:grid-cols-2" data-testid="accounting-report-top-jobs">
              {(m.vendors || []).filter((v) => v.top_jobs?.length).map((v) => (
                <div key={v.vendor} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50">{v.label} · top jobs</p>
                  <ul className="mt-1.5 divide-y divide-white/5">
                    {v.top_jobs.map((j) => (
                      <li key={j.job} className="flex items-center justify-between gap-2 py-1 text-xs">
                        <span className="truncate text-white/85">{j.job}</span>
                        <span className="shrink-0 font-mono tabular-nums text-white">{money(j.total)} <span className="text-white/40">· {j.count}</span></span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
